import type { BookingConfirmationSummary } from '@massilia/queries'

import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { Building2, Hash, User } from 'lucide-react'

import { CarRentalsSection } from '@/components/booking-sections/car-rentals-section'
import { ExtrasSection } from '@/components/booking-sections/extras-section'
import { HotelsSection } from '@/components/booking-sections/hotels-section'
import { InsuranceSection } from '@/components/booking-sections/insurance-section'
import { CopiableContactField } from '@/components/research-bar/copiable-contact-field'
import { BANK_ACCOUNT_HOLDER, BANK_BIC, BANK_IBAN, BANK_NAME } from '@/lib/constants/agency'

import type { PaymentConfirmationBookingData } from './payment-confirmation-booking-data'

import { CompleteYourTripSection } from './complete-your-trip-section'
import { PaymentConfirmationLayout } from './payment-confirmation-layout'
import { PaymentConfirmationSharedScreen } from './payment-confirmation-shared-screen'
import { paymentConfirmationVariants } from './payment-confirmation-variants'
import { SummaryRow } from './summary-row'
import { TripRecapSection } from './trip-recap-section'

interface PaymentPendingComponentProps {
  booking: PaymentConfirmationBookingData
  summary?: BookingConfirmationSummary
}

export function PaymentPendingComponent({ booking, summary }: PaymentPendingComponentProps) {
  const variant = paymentConfirmationVariants.pending

  return (
    <PaymentConfirmationLayout variant={variant}>
      <div className="border-gray-light-200 flex flex-col gap-3 rounded-2xl border bg-white p-6">
        <div className="text-gray-light-900 text-2xl font-semibold">{variant.title}</div>
        <p className="text-gray-light-700 text-sm">{variant.description}</p>

        <SummaryRow label="Numéro de dossier" value={booking.bookingRef} />
        <SummaryRow label="Adresse email" value={booking.customerEmail} />
        <SummaryRow label="Téléphone" value={booking.customerPhone} />
        <SummaryRow label="Tarif" value={booking.totalAmountLabel} />

        <div className="mt-2 flex flex-wrap gap-3">
          <Link to="/login">
            <Button type="button">Accéder à ma réservation</Button>
          </Link>
          <Button type="button" variant="outline">
            Conditions générales de ventes
          </Button>
        </div>
      </div>

      <TripRecapSection summary={summary} />

      {summary?.insurance && <InsuranceSection insurance={summary.insurance} />}
      {summary?.hotels && summary.hotels.length > 0 && <HotelsSection hotels={summary.hotels} />}
      {summary?.carRentals && summary.carRentals.length > 0 && (
        <CarRentalsSection carRentals={summary.carRentals} />
      )}
      {summary?.extras && summary.extras.length > 0 && <ExtrasSection extras={summary.extras} />}

      <div className="border-gray-light-200 flex flex-col gap-3 rounded-2xl border bg-white p-6">
        <div className="text-gray-light-900 text-2xl font-semibold">
          Règlement de votre réservation et modalités de paiements
        </div>

        {booking.depositDeadlineLabel && (
          <SummaryRow
            label="Date limite de réception du virement"
            value={booking.depositDeadlineLabel}
          />
        )}
        <SummaryRow label="Acomptes versés" value={booking.paidAmountLabel} />
        <SummaryRow label="Reste à payer" value={booking.remainingAmountLabel} />

        <div className="mt-1 grid grid-cols-1 gap-3 md:grid-cols-2">
          <CopiableContactField icon={User} label="Nom" value={BANK_ACCOUNT_HOLDER} />
          <CopiableContactField icon={Hash} label="IBAN" value={BANK_IBAN} />
          <CopiableContactField icon={Building2} label="Banque" value={BANK_NAME} />
          <CopiableContactField icon={Hash} label="Code banque" value={BANK_BIC} />
        </div>

        <div className="mt-1">
          <Button type="button">Payer un acompte</Button>
        </div>
      </div>

      <CompleteYourTripSection />

      <PaymentConfirmationSharedScreen
        departureAirportCode={summary?.flightSegments[0]?.departureLocation}
      />
    </PaymentConfirmationLayout>
  )
}
